import { z } from "zod";

/**
 * How a client authenticates at the token endpoint. Copilot Studio and some other tools send the
 * secret in the request body (`client_secret_post`); most others use Basic. Mirrors the auth-service
 * constant of the same name — the server refuses anything outside this pair.
 */
export const TOKEN_ENDPOINT_AUTH_METHODS = ["client_secret_basic", "client_secret_post"] as const;
export type TokenEndpointAuthMethod = (typeof TOKEN_ENDPOINT_AUTH_METHODS)[number];

export const TOKEN_ENDPOINT_AUTH_METHOD_LABELS: Record<TokenEndpointAuthMethod, string> = {
  client_secret_basic: "Request Header",
  client_secret_post: "Request Body",
};

/**
 * The client's auth method, or `undefined` for one this page can't switch, such as a
 * `private_key_jwt` client an operator registered. Absent means Basic, the provider's default.
 */
export function tokenEndpointAuthMethodOf(client: McpClient): TokenEndpointAuthMethod | undefined {
  const method = client.token_endpoint_auth_method ?? "client_secret_basic";
  return TOKEN_ENDPOINT_AUTH_METHODS.find((known) => known === method);
}

/**
 * The scopes every MCP credential is registered with, shown so the admin can paste them into their
 * tool. Mirrors auth-service's `MCP_CLIENT_SCOPES`, which the server pins regardless of what is sent.
 */
export const MCP_CLIENT_SCOPES = [
  "openid",
  "profile",
  "email",
  "offline_access",
  "mcp:read",
  "mcp:write",
] as const;

/** Everything an MCP credential needs: the authorization-code flow, with refresh tokens. */
export const MCP_CLIENT_GRANT_TYPES = ["authorization_code", "refresh_token"] as const;
export const MCP_CLIENT_RESPONSE_TYPES = ["code"] as const;

/** Stands in until the AI platform hands over its real callback URL. */
export const PLACEHOLDER_REDIRECT_URI = "https://placeholder.invalid/callback";

/** A redirect URI still set to the placeholder means the platform hasn't been pointed at us yet. */
export function hasRealRedirectUri(client: McpClient): boolean {
  return client.redirect_uris.some((uri) => uri !== PLACEHOLDER_REDIRECT_URI);
}

/**
 * Whether this page may edit the client's callback URL.
 *
 * The update endpoint replaces the whole list and this page only ever sends one value, so a client
 * registered with several — which the operator CLI can do, its `--redirect-uri` being variadic —
 * would silently lose the rest. Those are left to an operator rather than edited here.
 */
export function canEditRedirectUris(client: McpClient): boolean {
  return client.redirect_uris.length <= 1;
}

/**
 * Who created a credential, resolved by auth-service when it lists them. A former member is anyone
 * since deactivated, banned or deleted, and is not named. `stotles` means our staff created it, and
 * they are not named either.
 */
const createdBySchema = z.discriminatedUnion("type", [
  // `email` is optional so the page still renders against an auth-service that doesn't send it.
  z.object({ type: z.literal("member"), name: z.string(), email: z.string().optional() }),
  z.object({ type: z.literal("former_member") }),
  z.object({ type: z.literal("stotles") }),
]);

export type McpClientCreatedBy = z.infer<typeof createdBySchema>;

/**
 * The subset of the provider's RFC 7591 registration response this page reads. Unknown fields are
 * dropped rather than rejected, so an upstream addition can't break the settings page.
 */
export const mcpClientSchema = z.object({
  client_id: z.string(),
  client_name: z.string().optional(),
  redirect_uris: z.array(z.string()).default([]),
  token_endpoint_auth_method: z.string().optional(),
  // Unix seconds.
  client_id_issued_at: z.number().optional(),
  // Absent when nobody is recorded. A shape this page doesn't know reads as absent too.
  created_by: createdBySchema.optional().catch(undefined),
});

export type McpClient = z.infer<typeof mcpClientSchema>;

export const mcpClientListSchema = z.array(mcpClientSchema);

/**
 * A create or rotate response. The secret is returned in the one moment it exists in plaintext —
 * it is stored hashed, so a client that loses it has to rotate rather than look it up.
 */
export const mcpClientWithSecretSchema = mcpClientSchema.extend({
  client_secret: z.string(),
});

export type McpClientWithSecret = z.infer<typeof mcpClientWithSecretSchema>;

/**
 * Characters a tool name may contain: ASCII letters and digits, spaces, and `. _ + -`, starting with
 * a letter or digit. Mirrors auth-service's `TOOL_NAME_ALLOWED_CHARACTERS`, so the user sees the
 * message next to the field rather than as a failed request. Anything wider, such as bidi controls
 * or right-to-left text, could reorder the rendered name and hide the company suffix.
 */
export const TOOL_NAME_ALLOWED_CHARACTERS = /^[A-Za-z0-9][A-Za-z0-9 ._+-]*$/;

/**
 * Words a tool name can't contain, because they'd make the consent screen read as though the
 * credential came from Stotles. Mirrors auth-service's `TOOL_NAME_RESERVED_WORD`: "stotles" matches
 * anywhere, the rest only as whole words, so "Publicis" is fine.
 */
export const TOOL_NAME_RESERVED_WORD = /stotles|\b(?:admin|official|public|private)\b/i;

/** Matches the server's cap. */
export const MAX_TOOL_NAME_LENGTH = 60;

/**
 * The name a credential is registered under: the tool, then the company, so the consent screen
 * always says who registered it. auth-service refuses any other shape.
 */
export function mcpClientName(toolName: string, companyName: string): string {
  return `${toolName} - ${companyName}`;
}

export const toolNameSchema = z
  .string()
  .trim()
  .min(1, "Enter the name of the tool you're connecting")
  .max(MAX_TOOL_NAME_LENGTH, `Use ${MAX_TOOL_NAME_LENGTH} characters or fewer`)
  .refine(
    (name) => TOOL_NAME_ALLOWED_CHARACTERS.test(name),
    "Start with a letter or number, and use only English letters, numbers, spaces and . _ + -",
  )
  .refine(
    (name) => !TOOL_NAME_RESERVED_WORD.test(name),
    'Leave out "Stotles" and the words admin, official, public and private',
  );

/**
 * The only http callbacks accepted: a tool's local listener on 127.0.0.1, [::1] or localhost. Mirrors
 * the auth-service client-shape hook, which registers such a client as `native` rather than `web`.
 */
const LOCAL_HTTP_REDIRECT = /^http:\/\/(127\.0\.0\.1|\[::1\]|localhost)(?::\d+)?(?:[/?]|$)/i;

/**
 * How the provider matches a local callback's port: on any port for the IP literals, but only the
 * exact one registered for `localhost`. `undefined` for anything that isn't a local callback.
 */
export function localRedirectKind(uri: string): "any-port" | "exact-port" | undefined {
  const host = LOCAL_HTTP_REDIRECT.exec(uri.trim())?.[1];
  if (!host) return undefined;
  return host.toLowerCase() === "localhost" ? "exact-port" : "any-port";
}

/**
 * The application type auth-service requires for these callbacks: `native` for a local tool's
 * listener, `web` for a hosted one. The server refuses a mix of the two, and a mismatched type.
 */
export function applicationTypeFor(redirectUris: string[]): "web" | "native" {
  return redirectUris.every((uri) => localRedirectKind(uri)) ? "native" : "web";
}

export const redirectUriSchema = z
  .url({ protocol: /^https?$/, message: "Must be a valid HTTPS URL" })
  .superRefine((uri, ctx) => {
    if (uri.toLowerCase().startsWith("https:") || localRedirectKind(uri)) return;
    ctx.addIssue({
      code: "custom",
      message: "Must use HTTPS, except for http://localhost or http://127.0.0.1",
    });
  })
  .refine(
    (uri) => uri !== PLACEHOLDER_REDIRECT_URI,
    "Enter the callback URL from your AI platform",
  );
